/**
 * ResponseList Component
 * Displays member communication responses with read/unread status and follow-up flags
 * Reusable across enrollment detail, campaigns, and referrals
 */

import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  MessageSquare,
  Mail,
  Phone,
  CheckCircle,
  Clock,
  Flag,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  User,
  RefreshCw
} from 'lucide-react';
import { CommunicationResponse } from '@/hooks/useCommunicationResponses';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
interface ResponseListProps {
  responses: CommunicationResponse[];
  loading?: boolean;
  error?: string | null;
  unreadCount?: number;
  onMarkAsRead?: (responseId: number) => Promise<void>;
  onUpdateFollowUp?: (responseId: number, required: boolean, notes?: string) => Promise<void>;
  onRefresh?: () => Promise<void>;
  showMemberInfo?: boolean;
  emptyMessage?: string;
}

export const ResponseList: React.FC<ResponseListProps> = ({
  responses,
  loading = false,
  error = null,
  unreadCount = 0,
  onMarkAsRead,
  onUpdateFollowUp,
  onRefresh,
  showMemberInfo = false,
  emptyMessage = 'No responses yet'
}) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [processingId, setProcessingId] = useState<number | null>(null);

  const getChannelIcon = (channel: string) => {
    switch (channel.toUpperCase()) {
      case 'SMS':
        return <MessageSquare className="w-4 h-4 text-green-600" />;
      case 'WHATSAPP':
        return <MessageSquare className="w-4 h-4 text-green-500" />;
      case 'RCS':
        return <Phone className="w-4 h-4 text-indigo-600" />;
      case 'EMAIL':
        return <Mail className="w-4 h-4 text-blue-600" />;
      case 'VOICE':
      case 'PHONE':
        return <Phone className="w-4 h-4 text-orange-600" />;
      default:
        return <MessageSquare className="w-4 h-4" />;
    }
  };

  const getChannelBadgeStyle = (channel: string) => {
    switch (channel.toUpperCase()) {
      case 'WHATSAPP': return 'bg-green-50 text-green-700';
      case 'RCS': return 'bg-indigo-50 text-indigo-700';
      case 'SMS': return 'bg-green-100 text-green-700';
      case 'EMAIL': return 'bg-blue-100 text-blue-700';
      case 'VOICE': case 'PHONE': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getActionBadge = (action: string | null) => {
    if (!action) return null;

    const actionStyles: Record<string, string> = {
      accept: 'bg-green-100 text-green-800',
      reject: 'bg-red-100 text-red-800',
      reschedule: 'bg-yellow-100 text-yellow-800',
      unknown: 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${actionStyles[action] || actionStyles.unknown}`}>
        {action.charAt(0).toUpperCase() + action.slice(1)}
      </span>
    );
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return fmt.dateTime(timestamp);
  };

  const formatResponseDelay = (seconds: number | null) => {
    if (!seconds) return null;

    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d`;
    if (hours > 0) return `${hours}h`;
    return `${minutes}m`;
  };

  const handleMarkAsRead = async (responseId: number) => {
    if (!onMarkAsRead) return;
    setProcessingId(responseId);
    try {
      await onMarkAsRead(responseId);
    } finally {
      setProcessingId(null);
    }
  };

  const handleToggleFollowUp = async (response: CommunicationResponse) => {
    if (!onUpdateFollowUp) return;
    setProcessingId(response.id);
    try {
      await onUpdateFollowUp(response.id, !response.follow_up_required);
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <RefreshCw className="w-5 h-5 animate-spin text-gray-400 mr-2" />
          <span className="text-gray-500">{t('communications.loading_responses', 'Loading responses...')}</span>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center text-red-600">
          <p>Error loading responses: {error}</p>
          {onRefresh && (
            <Button variant="outline" size="sm" onClick={onRefresh} className="mt-2">
              <RefreshCw className="w-4 h-4 mr-1" />
              {t('communications.retry', 'Retry')}
            </Button>
          )}
        </div>
      </Card>
    );
  }

  if (responses.length === 0) {
    return (
      <Card className="p-6">
        <div className="text-center text-gray-500">
          <MessageSquare className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p>{emptyMessage}</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header with stats */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-600">
            {responses.length} response{responses.length !== 1 ? 's' : ''}
          </span>
          {unreadCount > 0 && (
            <Badge variant="default" className="bg-blue-500">
              {unreadCount} unread
            </Badge>
          )}
        </div>
        {onRefresh && (
          <Button variant="ghost" size="sm" onClick={onRefresh}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Response list */}
      {responses.map((response) => (
        <Card
          key={response.id}
          className={`p-4 transition-all ${
            !response.is_read ? 'border-l-4 border-l-blue-500 bg-blue-50/50' : ''
          } ${response.follow_up_required ? 'border-r-4 border-r-orange-500' : ''}`}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {/* Header row */}
              <div className="flex items-center gap-2 mb-2">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getChannelBadgeStyle(response.response_channel)}`}>
                  {getChannelIcon(response.response_channel)}
                  {response.response_channel}
                </span>
                {getActionBadge(response.response_action)}
                {!response.is_read && (
                  <Badge variant="secondary" className="bg-blue-100 text-blue-800 text-xs">
                    {t('communications.new', 'New')}
                  </Badge>
                )}
                {response.follow_up_required && (
                  <Badge variant="secondary" className="bg-orange-100 text-orange-800 text-xs">
                    <Flag className="w-3 h-3 mr-1" />
                    {t('communications.followup', 'Follow-up')}
                  </Badge>
                )}
              </div>

              {/* Member info if shown */}
              {showMemberInfo && response.member_name && (
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                  <User className="w-3 h-3" />
                  <span>{response.member_name}</span>
                  {response.member_phone && (
                    <span className="text-gray-400">| {response.member_phone}</span>
                  )}
                </div>
              )}

              {/* Response content preview or full */}
              {response.response_content && (
                <div className="mt-2">
                  <p className={`text-sm text-gray-800 ${
                    expandedId === response.id ? '' : 'line-clamp-2'
                  }`}>
                    {response.response_content}
                  </p>
                  {response.response_content.length > 100 && (
                    <button
                      onClick={() => setExpandedId(expandedId === response.id ? null : response.id)}
                      className="text-xs text-blue-600 hover:text-blue-800 mt-1 flex items-center"
                    >
                      {expandedId === response.id ? (
                        <>
                          <ChevronUp className="w-3 h-3 mr-1" />
                          {t('communications.show_less', 'Show less')}
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-3 h-3 mr-1" />
                          {t('communications.show_more', 'Show more')}
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}

              {/* Timestamp and delay */}
              <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                <span className="flex items-center">
                  <Clock className="w-3 h-3 mr-1" />
                  {formatTimestamp(response.response_timestamp)}
                </span>
                {response.response_delay_seconds && (
                  <span className="text-gray-400">
                    Response time: {formatResponseDelay(response.response_delay_seconds)}
                  </span>
                )}
                {response.is_read && response.read_at && (
                  <span className="flex items-center text-green-600">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {t('communications.read', 'Read')}
                  </span>
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-1 ml-4">
              {!response.is_read && onMarkAsRead && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleMarkAsRead(response.id)}
                  disabled={processingId === response.id}
                  className="text-xs"
                >
                  <Eye className="w-3 h-3 mr-1" />
                  {t('communications.mark_read', 'Mark read')}
                </Button>
              )}
              {onUpdateFollowUp && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleToggleFollowUp(response)}
                  disabled={processingId === response.id}
                  className={`text-xs ${response.follow_up_required ? 'text-orange-600' : ''}`}
                >
                  <Flag className="w-3 h-3 mr-1" />
                  {response.follow_up_required ? 'Clear flag' : 'Flag'}
                </Button>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default ResponseList;
